import { useMemo } from "react";
import { Tooltip } from "antd";
import dayjs from "dayjs";
import {
  DAY_LABELS,
  DAY_MIN,
  WEEK_MIN,
  assignStaffColors,
  buildShiftBars,
  computeSegments,
  formatHours,
  formatWeekMinute,
  initials,
  packLanes,
  type GroupDays,
} from "./coverageMath";

const LABEL_W = 200;
const LANE_H = 18;
const LANE_GAP = 2;
const OVERLAP_H = 8;
const GAP_H = 12;
const HEADER_H = 34;

const NONE_BG = "repeating-linear-gradient(45deg, #ff4d4f 0 4px, #ffccc7 4px 8px)";
const PARTIAL_BG = "repeating-linear-gradient(45deg, #fa8c16 0 4px, #ffe7ba 4px 8px)";

interface MachineGroup {
  name: string;
  machines?: string[];
  days?: GroupDays;
}

interface Props {
  groups: MachineGroup[];
  /** The previous week's groups, used only for Saturday-night carry-over. */
  prevGroups?: MachineGroup[];
  weekStart: dayjs.Dayjs;
}

const pct = (min: number) => `${(min / WEEK_MIN) * 100}%`;
const widthPct = (start: number, end: number) => `${((end - start) / WEEK_MIN) * 100}%`;

export default function MachineGroupWeekTimeline({ groups, prevGroups, weekStart }: Props) {
  const rows = useMemo(() => {
    const prevByName = new Map((prevGroups || []).map((g) => [g.name, g.days]));
    return groups.map((group) => {
      const { bars, laneCount } = packLanes(buildShiftBars(group.days, prevByName.get(group.name)));
      const totalMachines = (group.machines || []).length;
      const { gaps, overlaps } = computeSegments(bars, totalMachines);
      return { group, bars, laneCount, gaps, overlaps, totalMachines };
    });
  }, [groups, prevGroups]);

  const staffColors = useMemo(() => assignStaffColors(rows.map((row) => row.bars)), [rows]);

  const todayIndex = dayjs().startOf("day").diff(weekStart.startOf("day"), "day");

  return (
    <div style={{ border: "1px solid #e8e8e8", borderRadius: 6, background: "#fff" }}>
      {/* Legend */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12, padding: "8px 12px", borderBottom: "1px solid #f0f0f0", fontSize: 11 }}>
        {Array.from(staffColors.entries()).map(([id, { name, color }]) => (
          <span key={id} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 12, height: 12, borderRadius: 2, background: color, display: "inline-block" }} />
            {name}
          </span>
        ))}
        <span style={{ marginLeft: "auto", display: "inline-flex", gap: 12, color: "#666" }}>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 16, height: 10, background: NONE_BG, display: "inline-block" }} /> Unattended
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 16, height: 10, background: PARTIAL_BG, display: "inline-block" }} /> Partly attended (approx.)
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 16, height: 8, background: "#595959", display: "inline-block" }} /> Overlap
          </span>
        </span>
      </div>

      {/* Day header */}
      <div style={{ display: "flex", position: "sticky", top: 0, zIndex: 3, background: "#fafafa", borderBottom: "2px solid #d9d9d9" }}>
        <div style={{ width: LABEL_W, minWidth: LABEL_W, padding: "6px 8px", fontWeight: 600, fontSize: 11, borderRight: "1px solid #e8e8e8" }}>
          Machine Group
        </div>
        <div style={{ flex: 1, display: "flex", height: HEADER_H }}>
          {DAY_LABELS.map((day, i) => (
            <div
              key={day}
              style={{
                flex: 1,
                borderRight: i < 6 ? "1px solid #d9d9d9" : undefined,
                background: i === todayIndex ? "#e6f4ff" : i === 0 || i === 6 ? "#f5f5f5" : undefined,
                position: "relative",
                fontSize: 11,
              }}
            >
              <div style={{ textAlign: "center", fontWeight: 600, paddingTop: 2 }}>
                {day} {weekStart.add(i, "day").format("M/D")}
              </div>
              <div style={{ position: "absolute", bottom: 1, left: 0, right: 0, fontSize: 9, color: "#999" }}>
                {[6, 12, 18].map((h) => (
                  <span key={h} style={{ position: "absolute", left: `${(h / 24) * 100}%`, transform: "translateX(-50%)" }}>
                    {h}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {rows.map(({ group, bars, laneCount, gaps, overlaps, totalMachines }) => {
        const lanesH = laneCount * (LANE_H + LANE_GAP);
        const stripH = lanesH + OVERLAP_H + GAP_H + 10;
        const noCoverage = bars.filter((b) => !b.onVacation).length === 0;

        return (
          <div key={group.name} style={{ display: "flex", borderBottom: "1px solid #e8e8e8" }}>
            <div style={{ width: LABEL_W, minWidth: LABEL_W, padding: "6px 8px", borderRight: "1px solid #e8e8e8" }}>
              <div style={{ fontWeight: 600, fontSize: 12 }}>{group.name}</div>
              <div style={{ fontSize: 10, color: "#888" }}>
                {totalMachines > 0 ? (group.machines || []).join(", ") : "No active machines"}
              </div>
              {noCoverage && totalMachines > 0 && (
                <div style={{ fontSize: 10, color: "#cf1322", fontWeight: 600, marginTop: 2 }}>
                  No coverage this week
                </div>
              )}
            </div>

            <div style={{ flex: 1, position: "relative", height: stripH, minHeight: 44 }}>
              {/* Day dividers, today highlight and 6-hour ticks */}
              {DAY_LABELS.map((day, i) => (
                <div
                  key={day}
                  style={{
                    position: "absolute",
                    top: 0,
                    bottom: 0,
                    left: pct(i * DAY_MIN),
                    width: pct(DAY_MIN),
                    borderRight: i < 6 ? "1px solid #d9d9d9" : undefined,
                    background: i === todayIndex ? "rgba(22,119,255,0.05)" : undefined,
                  }}
                >
                  {[6, 12, 18].map((h) => (
                    <div
                      key={h}
                      style={{ position: "absolute", top: 0, bottom: 0, left: `${(h / 24) * 100}%`, borderLeft: "1px dashed #f0f0f0" }}
                    />
                  ))}
                </div>
              ))}

              {/* Shift bars */}
              {bars.map((bar) => {
                const color = staffColors.get(bar.staffId)?.color || "#8c8c8c";
                const duration = bar.rawEndMin - bar.rawStartMin;
                const partial = bar.machinesCount < bar.totalMachines;
                const label = duration >= 360 ? bar.staff : initials(bar.staff);
                return (
                  <Tooltip
                    key={bar.key}
                    title={
                      <div style={{ fontSize: 12 }}>
                        <div style={{ fontWeight: 600 }}>{bar.staff}</div>
                        <div>
                          {DAY_LABELS[bar.dayIndex]} {bar.startLabel}–{bar.endLabel} ({formatHours(duration)})
                          {bar.carriedOver ? " (from previous week)" : ""}
                          {bar.rawEndMin > WEEK_MIN ? " · continues into next week" : ""}
                        </div>
                        <div>Machines: {bar.machinesCount}/{bar.totalMachines}</div>
                        {bar.onVacation && <div style={{ color: "#ffa39e" }}>On vacation — not counted as coverage</div>}
                      </div>
                    }
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 4 + bar.lane * (LANE_H + LANE_GAP),
                        left: pct(bar.startMin),
                        width: widthPct(bar.startMin, bar.endMin),
                        minWidth: 3,
                        height: LANE_H,
                        borderRadius: 3,
                        background: bar.onVacation
                          ? `repeating-linear-gradient(45deg, #fff 0 4px, ${color}33 4px 8px)`
                          : color,
                        border: bar.onVacation ? `1px dashed ${color}` : undefined,
                        color: bar.onVacation ? "#8c8c8c" : "#fff",
                        textDecoration: bar.onVacation ? "line-through" : undefined,
                        fontSize: 10,
                        lineHeight: `${LANE_H}px`,
                        padding: "0 4px",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                        textOverflow: "ellipsis",
                        boxSizing: "border-box",
                        cursor: "default",
                        zIndex: 2,
                      }}
                    >
                      {label}
                      {partial && ` [${bar.machinesCount}/${bar.totalMachines}]`}
                    </div>
                  </Tooltip>
                );
              })}

              {/* Overlap band */}
              {overlaps.map((o) => (
                <Tooltip
                  key={`o-${o.startMin}`}
                  title={`${o.count} people on ${formatWeekMinute(o.startMin)}–${formatWeekMinute(o.endMin)} (${formatHours(o.endMin - o.startMin)})`}
                >
                  <div
                    style={{
                      position: "absolute",
                      top: 4 + lanesH,
                      left: pct(o.startMin),
                      width: widthPct(o.startMin, o.endMin),
                      minWidth: 2,
                      height: OVERLAP_H,
                      background: o.count >= 3 ? "#262626" : "#595959",
                      color: "#fff",
                      fontSize: 8,
                      lineHeight: `${OVERLAP_H}px`,
                      textAlign: "center",
                      overflow: "hidden",
                      zIndex: 2,
                    }}
                  >
                    {o.count}x
                  </div>
                </Tooltip>
              ))}

              {/* Gap band */}
              {totalMachines > 0 &&
                gaps.map((g) => (
                  <Tooltip
                    key={`g-${g.startMin}`}
                    title={
                      g.kind === "none"
                        ? `Unattended ${formatWeekMinute(g.startMin)}–${formatWeekMinute(g.endMin)} (${formatHours(g.endMin - g.startMin)})`
                        : `Partly attended ${formatWeekMinute(g.startMin)}–${formatWeekMinute(g.endMin)} (${formatHours(g.endMin - g.startMin)}): about ${g.coveredMachines} of ${totalMachines} machines covered`
                    }
                  >
                    <div
                      style={{
                        position: "absolute",
                        top: 6 + lanesH + OVERLAP_H,
                        left: pct(g.startMin),
                        width: widthPct(g.startMin, g.endMin),
                        minWidth: 2,
                        height: GAP_H,
                        borderRadius: 2,
                        background: g.kind === "none" ? NONE_BG : PARTIAL_BG,
                        zIndex: 2,
                      }}
                    />
                  </Tooltip>
                ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
